package link.erpango.quip.core.interactor

import kotlinx.coroutines.CoroutineScope
import link.erpango.quip.core.auth.IFirebaseUserWrapper
import link.erpango.quip.data.IDataSource
import link.erpango.quip.data.firebase.auth.FirebaseAuth
import link.erpango.quip.data.remote.DResponse
import link.erpango.quip.domain.entities.DAuth
import link.erpango.quip.domain.usecases.IAuthUseCase
import link.erpango.quip.utils.DatetimeUtils
import kotlin.coroutines.CoroutineContext
import kotlinx.coroutines.launch

class AuthInteractor(
    override val data: IDataSource
): IAuthUseCase, IInteractor {
    val userStateFlow: IFirebaseUserWrapper? = data.firebase.auth.user

    override suspend fun loginWithGoogle(idToken: String, scope: CoroutineScope): DAuth? {
        return data.remote.auth.loginWithGoogle(idToken).let {
            if (it is DResponse.Success) {
                it.data
            } else null

        }
    }

    override suspend fun deleteAllTokens() {
        data.db.auth.deleteAllTokens()
    }

    override fun authFlow(scope: CoroutineContext) = data.db.auth.observeAuth(scope)

    /**
     * Obtiene la autenticación guardada del usuario actual si existe.
     * @return DAuth? objeto de autenticación o null si no existe
     */
    override suspend fun getUserAuth(): DAuth? {
        // Obtener el primer token de autenticación guardado en la base de datos
        return data.db.auth.getFirstAuth()
    }

    override suspend fun saveCurrentUser(auth: DAuth) {
        //Intentamos insertar/actualizar el usuario en la base de datos
        auth.user?.let {
            data.remote.user.getUser(it)?.let {
                if (it is DResponse.Success) {
                    data.db.user.insertUser(
                        id = it.data.id ?: throw Exception("No user id received"),
                        name = it.data.name,
                        email = it.data.email ?: throw Exception("No user email received"),
                        avatar = it.data.avatar
                    )
                }
            }
        }
    }


    override suspend fun saveUserAuth(auth: DAuth) {
        // insert or update token
        auth.user?.let {
            val exists = data.db.auth.select(auth.user)
            if (exists != null) {
                data.db.auth.updateToken(
                    auth.user,
                    auth.access ?: throw Exception("Invalid token"),
                    auth.refresh ?: "",
                    auth.createdAt ?: DatetimeUtils.currentTimeMillis()
                )
            } else {
                // El usuario logeado es el único que puede tener un token
                data.db.auth.deleteAllTokens()
                data.db.auth.insertToken(
                    auth.user ?: throw Exception("No user id received"),
                    auth.access ?: throw Exception("Invalid token"),
                    auth.refresh ?: "",
                    auth.createdAt ?: DatetimeUtils.currentTimeMillis()
                )
            }
            
            // Try to sync pending FCM token
            syncDeviceToken()
            
        } ?: throw Exception("No user id received")

    }

    override fun getFirebaseAuthAccesor(): FirebaseAuth {
        return data.firebase.auth
    }

    override suspend fun signIn(email: String, pass: String): Boolean {
        val response = data.remote.auth.login(email, pass)
        return if (response is DResponse.Success) {
            saveUserAuth(response.data)
            saveCurrentUser(response.data)
            true
        } else {
            false
        }
    }
    

    override fun signOut(scope: CoroutineScope) {
        scope.launch {
            // data.remote.auth.logout() // Optional: call backend logout
            data.firebase.auth.signOut(scope)
            deleteAllLocalData()
        }
    }

    override fun isUserLogged(scope: CoroutineScope): Boolean {
        // Check local DB for session
        // return data.db.auth.getFirstAuth() != null
        return data.firebase.auth.getCurrentUser() != null // TODO: Update to check local DB strictly if needed
    }

    override fun signInWithGoogle(scope: CoroutineScope) {
        // This is triggered from UI for Firebase Google Sign In flow (web/android)
        // Actual backend auth happens in loginWithGoogle(idToken)
    }

    override fun signInWithApple(scope: CoroutineScope) { }
    override fun signInWithFacebook(scope: CoroutineScope) { }
    override fun signInWithTwitter(scope: CoroutineScope) { }
    override fun signInWithGithub(scope: CoroutineScope) { }
    override fun signInWithMicrosoft(scope: CoroutineScope) { }


    
    override suspend fun register(name: String, email: String, pass: String): Boolean {
        val response = data.remote.auth.register(name, email, pass)
        return if (response is DResponse.Success) {
            saveUserAuth(response.data)
            saveCurrentUser(response.data)
            true
        } else {
            false
        }
    }


    override suspend fun registerDeviceToken(token: String, platform: String) {
        // 1. Cache the token locally
        data.db.commonValues.insertValue("fcm_token", token, DatetimeUtils.currentTimeMillis())
        data.db.commonValues.insertValue("fcm_platform", platform, DatetimeUtils.currentTimeMillis())
        
        // 2. Try to sync with backend
        syncDeviceToken()
    }
    
    private suspend fun syncDeviceToken() {
        val userAuth = getUserAuth()
        if (userAuth != null) {
            val token = data.db.commonValues.selectValue("fcm_token")
            val platform = data.db.commonValues.selectValue("fcm_platform")
            
            if (!token.isNullOrBlank() && !platform.isNullOrBlank()) {
                println("Syncing FCM token for user ${userAuth.user}...")
                val response = data.remote.auth.registerDevice(token, platform, null)
                if (response is DResponse.Error) {
                     println("Failed to register device token: ${response.error}")
                } else {
                    println("Device token registered successfully for $platform")
                }
            }
        } else {
            println("User not logged in, caching FCM token for later sync.")
        }
    }

    override suspend fun fetchAndSyncNotificationSettings(): link.erpango.quip.domain.entities.DNotificationSettingsRequest? {
        val response = data.remote.auth.fetchNotificationSettings()
        return if (response is DResponse.Success) {
            val s = response.data
            saveNotificationSettings(s.notificationsEnabled)
            saveItvNotificationSettings(s.itvNotificationsEnabled)
            saveRevisionNotificationSettings(s.revisionNotificationsEnabled)
            saveSparePartNotificationSettings(s.sparePartNotificationsEnabled)
            s
        } else null
    }

    override suspend fun updateNotificationSettings(maintenance: Boolean, itv: Boolean, revision: Boolean, sparePart: Boolean): Boolean {
        saveNotificationSettings(maintenance)
        saveItvNotificationSettings(itv)
        saveRevisionNotificationSettings(revision)
        saveSparePartNotificationSettings(sparePart)
        val response = data.remote.auth.updateNotificationSettings(maintenance, itv, revision, sparePart)
        return response is DResponse.Success
    }

    override suspend fun getNotificationSettings(): Boolean {
        val value = data.db.commonValues.selectValue("notifications_enabled")
        return value?.toBoolean() ?: true
    }

    override suspend fun saveNotificationSettings(enabled: Boolean) {
        data.db.commonValues.insertValue(
            "notifications_enabled",
            enabled.toString(),
            DatetimeUtils.currentTimeMillis()
        )
    }

    override suspend fun getItvNotificationSettings(): Boolean {
        val value = data.db.commonValues.selectValue("itv_notifications_enabled")
        return value?.toBoolean() ?: true
    }

    override suspend fun saveItvNotificationSettings(enabled: Boolean) {
        data.db.commonValues.insertValue(
            "itv_notifications_enabled",
            enabled.toString(),
            DatetimeUtils.currentTimeMillis()
        )
    }

    override suspend fun getRevisionNotificationSettings(): Boolean {
        val value = data.db.commonValues.selectValue("revision_notifications_enabled")
        return value?.toBoolean() ?: true
    }

    override suspend fun saveRevisionNotificationSettings(enabled: Boolean) {
        data.db.commonValues.insertValue(
            "revision_notifications_enabled",
            enabled.toString(),
            DatetimeUtils.currentTimeMillis()
        )
    }

    override suspend fun getSparePartNotificationSettings(): Boolean {
        val value = data.db.commonValues.selectValue("spare_part_notifications_enabled")
        return value?.toBoolean() ?: true
    }

    override suspend fun saveSparePartNotificationSettings(enabled: Boolean) {
        data.db.commonValues.insertValue(
            "spare_part_notifications_enabled",
            enabled.toString(),
            DatetimeUtils.currentTimeMillis()
        )
    }

    /**
     * Borra todos los datos locales de la base de datos.
     * Se utiliza al cerrar sesión para limpiar completamente los datos del usuario.
     */
    override suspend fun deleteAllLocalData() {
        data.db.deleteAllData()
    }
}